import React, { useEffect, useMemo, useState, useCallback } from "react";
import styles from "./ProductIntroduceList.module.css";
import Axios from "@/Axios";
import { useCheckLogin } from "@/hooks";
import { INSERT_TAG } from "@/pages";
import { useNavigate } from "react-router-dom";
import {
  BottomNavigationBar,
  Header,
  ProductsList,
  useConfirm,
} from "@/components";
import {
  useLoadReset,
  useDebouncedCallback,
  useDimLoading,
  useDimLoadComplete,
} from "@/hooks";

export default function ProductIntroduceList() {
  const dimLoading = useDimLoading();
  const dimLoadComplete = useDimLoadComplete();
  const loadReset = useLoadReset();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const defaultState = {
    isLogin: false,
    isUpdate: false,
    isDelete: false,
    isDim: false,
    list: [],
    deletes: [],
  };
  const [state, setState] = useState(defaultState);
  const checkLogin = useCheckLogin();
  const resetState = () => {
    setState(() => ({ ...defaultState }));
    loadReset();
  };
  const turnIntoUpdate = () => {
    setState((s) => ({
      ...s,
      isUpdate: true,
    }));
  };
  const updateToNormal = () => {
    setState((s) => ({
      ...s,
      isUpdate: false,
    }));
  };
  const turnIntoDelete = () => {
    setState((s) => ({
      ...s,
      isDelete: true,
      isReset: false,
      isDim: true,
    }));
  };
  const deleteToNormal = () => {
    setState((s) => ({
      ...s,
      isDelete: false,
      isReset: true,
      isDim: false,
    }));
  };
  const getAllList = () => {
    Axios.get("/api/product-list", { params: {} }).then((res) => {
      if (res.data?.errMsg) {
        confirm(`${res.data.errMsg}\n(${res.data.errCode})`);
        return;
      }
      if (!res.data?.productLists) {
        setState((s) => ({ ...s, list: [] }));
        return;
      }
      const list = res.data?.productLists.map((data) => ({
        ...data,
        id: data.index,
        title: data.productNm,
      }));
      setState((s) => ({ ...s, list }));
    });
  };

  const toInsert = () => {
    navigate(`/product-upsert/${INSERT_TAG}`);
  };

  const deleteInfo = useDebouncedCallback(() => {
    confirm("정말로 삭제하시겠습니까?", () => {
      if (!state.deletes?.length) {
        confirm("삭제할 대상이 존재하지 않습니다.");
        return;
      }

      dimLoading();
      Axios.post("/api/product-list/delete", {
        productLists: state.deletes,
      }).then((res) => {
        if (res.data?.errMsg) {
          dimLoadComplete(() => {
            confirm(`${res.data.errMsg}\n(${res.data.errCode})`);
          });
          return;
        }
        setState((s) => ({ ...defaultState, isLogin: s.isLogin }));
        getAllList();
        dimLoadComplete(() => {
          confirm("삭제를 완료했습니다.");
        });
      });
    });
  });
  const GROUP_BUY_LIST = useMemo(() => {
    return [...state.list].filter((l) => l.isGroupBuy);
  }, [state.list]);
  const RECOMMEND_LIST = useMemo(() => {
    return [...state.list].filter((l) => !l.isGroupBuy);
  }, [state.list]);
  const setDeletes = useCallback((d) => {
    setState((s) => ({ ...s, deletes: [...s?.deletes, d] }));
  }, []);
  const resetDeletes = useCallback((d) => {
    setState((s) => ({
      ...s,
      deletes: [...s?.deletes].filter((obj) => obj.index !== d.index),
    }));
  }, []);

  useEffect(() => {
    const callbackId = checkLogin(
      () => {
        setState((s) => ({ ...s, isLogin: true }));
      },
      () => {
        setState((s) => ({ ...s, isLogin: false }));
      }
    );
    getAllList();
    return () => {
      resetState();
      cancelIdleCallback(callbackId);
    };
  }, []);
  return (
    <div className={styles.mainContainer}>
      <div className={styles.contentArea}>
        <div className={styles.headerContainer}>
          <Header title={"상품 소개"} />
          {state.isDim && <div className={styles.topDimmed} />}
        </div>
        {state.isLogin && !state.isUpdate && !state.isDelete && (
          <div className={styles.buttonArea}>
            <button className={styles.updateBtn} onClick={toInsert}>
              <span className={styles.update}>등록</span>
            </button>
            <button className={styles.updateBtn} onClick={turnIntoUpdate}>
              <span className={styles.update}>수정</span>
            </button>
            <button className={styles.deleteBtn} onClick={turnIntoDelete}>
              <span className={styles.delete}>삭제</span>
            </button>
          </div>
        )}
        {state.isUpdate && (
          <div className={styles.buttonArea}>
            <div className={styles.deleting}>
              <span className={styles.deleting1}>수정중...</span>
            </div>
            <div className={styles.deleting}></div>
            <button className={styles.cancelBtn} onClick={updateToNormal}>
              <span className={styles.cancel}>취소</span>
            </button>
          </div>
        )}
        {state.isDelete && (
          <div className={styles.buttonArea}>
            <div className={styles.deleting}>
              <span className={styles.deleting1}>삭제중...</span>
            </div>
            <button className={styles.deleteBtn} onClick={deleteInfo}>
              <span className={styles.delete}>삭제</span>
            </button>
            <button className={styles.cancelBtn} onClick={deleteToNormal}>
              <span className={styles.cancel}>취소</span>
            </button>
          </div>
        )}
        <div className={styles.contentInnerContainer}>
          {GROUP_BUY_LIST.length > 0 && (
            <ProductsList
              key={"groupBuy"}
              title={"공동 구매"}
              list={GROUP_BUY_LIST}
              defaultUrl={"/product-list"}
              updateUrl={"/product-upsert"}
              isUpdate={state.isUpdate}
              isDelete={state.isDelete}
              isReset={state.isReset}
              setDeletes={setDeletes}
              resetDeletes={resetDeletes}
            />
          )}
          <ProductsList
            key={"introduce"}
            title={"상품 추천"}
            list={RECOMMEND_LIST}
            defaultUrl={"/product-list"}
            updateUrl={"/product-upsert"}
            isUpdate={state.isUpdate}
            isDelete={state.isDelete}
            isReset={state.isReset}
            setDeletes={setDeletes}
            resetDeletes={resetDeletes}
          />
        </div>
      </div>
      <div className={styles.bottomContainer}>
        <BottomNavigationBar />
        {state.isDim && <div className={styles.bottomDimmed} />}
      </div>
    </div>
  );
}
